import { useState, type CSSProperties, type ReactNode } from "react";
import { useEmbedTokens } from "../theme";
import { useContainerWidth } from "../use-container-width";

export interface EmbedTableColumn<Row> {
  id: string;
  header: string;
  cell: (row: Row) => ReactNode;
  align?: "start" | "end";
  /** Right-align with tabular numerals. */
  numeric?: boolean;
  /** The column that leads each row once the table is stacked into a list. */
  primary?: boolean;
  /** Hide this column in the wide table below this container width. */
  hideBelowPx?: number;
  /** Label for the stacked row; `false` shows the value with no label. Defaults to `header`. */
  stackedLabel?: string | false;
}

interface Props<Row> {
  caption: string;
  captionVisible?: boolean;
  columns: EmbedTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Container width below which the table stacks into a list. Defaults to the DS narrow breakpoint. */
  stackBelowPx?: number;
  rowTestId?: (row: Row) => string;
}

const VISUALLY_HIDDEN: CSSProperties = {
  position: "absolute", width: 1, height: 1, margin: -1, padding: 0,
  overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
};

/**
 * A data table that becomes a stacked list when its CONTAINER is narrow (never
 * the viewport: the embed lives in a host page column, see useContainerWidth).
 * Wide: a real <table> with a caption and scoped column headers. Narrow: a
 * labelled <ul> where each row leads with its primary cell, then label/value
 * pairs. An unmeasured width renders the wide layout.
 */
export function EmbedTable<Row>({ caption, captionVisible = false, columns, rows, rowKey, stackBelowPx, rowTestId }: Props<Row>) {
  const t = useEmbedTokens();
  const [ref, width] = useContainerWidth();
  const stackAt = stackBelowPx ?? t.size.tableStackBelow;
  // width > 0: a display:none container reports 0x0 on first observation, which
  // would otherwise read as narrow and snap to the wide layout once revealed.
  const stacked = width !== null && width > 0 && width < stackAt;

  const cellText: CSSProperties = { overflowWrap: "anywhere", minWidth: 0 };

  if (stacked) {
    const primary = columns.find((c) => c.primary) ?? columns[0];
    const rest = columns.filter((c) => c !== primary);
    return (
      <div ref={ref} style={{ fontFamily: t.fontFamily }}>
        <ul role="list" aria-label={caption} style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {rows.map((row) => (
            <li key={rowKey(row)} data-testid={rowTestId?.(row)}
              style={{ padding: `${t.spacing.md}px 0`, borderTop: `1px solid ${t.colors.borderSubtle}` }}>
              <div style={{ ...cellText, color: t.colors.text, fontSize: t.fontSize.sm, fontWeight: t.fontWeight.medium }}>
                {primary?.cell(row)}
              </div>
              {rest.filter((c) => c.stackedLabel === false).map((c) => (
                // An unlabelled value is not a term/description pair, so it
                // sits outside the <dl> rather than as an orphan <dd>.
                <div key={c.id} style={{ ...cellText, marginTop: t.spacing.xs, fontSize: t.fontSize.sm, color: t.colors.text }}>{c.cell(row)}</div>
              ))}
              {rest.some((c) => c.stackedLabel !== false) && (
              <dl style={{ margin: `${t.spacing.xs}px 0 0`, display: "grid", gridTemplateColumns: "minmax(0, max-content) minmax(0, 1fr)", columnGap: t.spacing.md, rowGap: 2 }}>
                {rest.filter((c) => c.stackedLabel !== false).map((c) => (
                  <div key={c.id} style={{ display: "contents" }}>
                    <dt style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>{c.stackedLabel ?? c.header}</dt>
                    <dd style={{ ...cellText, margin: 0, fontSize: t.fontSize.sm, color: t.colors.text }}>{c.cell(row)}</dd>
                  </div>
                ))}
              </dl>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const visible = columns.filter((c) => c.hideBelowPx === undefined || width === null || width >= c.hideBelowPx);
  const alignOf = (c: EmbedTableColumn<Row>): "left" | "right" => (c.numeric || c.align === "end" ? "right" : "left");
  return (
    <div ref={ref} style={{ fontFamily: t.fontFamily }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
        <caption style={captionVisible
          ? { textAlign: "left", fontSize: t.fontSize.md, fontWeight: t.fontWeight.medium, color: t.colors.text, padding: `0 0 ${t.spacing.sm}px` }
          : VISUALLY_HIDDEN}>{caption}</caption>
        <thead>
          <tr>
            {visible.map((c) => (
              <th key={c.id} scope="col" style={{
                ...cellText, textAlign: alignOf(c), padding: "10px 12px",
                color: t.colors.textSecondary, fontSize: t.fontSize.xs, fontWeight: t.fontWeight.medium,
                borderBottom: `1px solid ${t.colors.borderSubtle}`,
              }}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow key={rowKey(row)} row={row} columns={visible} testId={rowTestId?.(row)} alignOf={alignOf} cellText={cellText} />
          ))}
        </tbody>
      </table>
    </div>
  );
}


function TableRow<Row>({ row, columns, testId, alignOf, cellText }: {
  row: Row; columns: EmbedTableColumn<Row>[]; testId?: string;
  alignOf: (c: EmbedTableColumn<Row>) => "left" | "right"; cellText: CSSProperties;
}) {
  const t = useEmbedTokens();
  const [hover, setHover] = useState(false);
  return (
    <tr data-testid={testId} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ background: hover ? t.colors.surfaceHover : "transparent" }}>
      {columns.map((c) => (
        <td key={c.id} style={{
          ...cellText, textAlign: alignOf(c), padding: "10px 12px", verticalAlign: "middle",
          color: t.colors.text, fontSize: t.fontSize.sm,
          borderBottom: `1px solid ${t.colors.borderSubtle}`,
          ...(c.numeric ? { fontVariantNumeric: "tabular-nums" } : {}),
        }}>{c.cell(row)}</td>
      ))}
    </tr>
  );
}
